import TabletopUniverse from "../objects/TabletopUniverse";
import TabletopViewport from "../objects/TabletopViewport";
import useCanvas from "../../../common/hooks/RenderingHooks";

import { Point2d } from "../../../common/geometry/Geom2d";
import {
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTabletopCanvasInputHandler } from "../controls/TabletopCanvasInputHandler";
import TabletopPositionalElement from "../objects/element/TabletopPositionalElement";
import TabletopHexGrid from "../objects/grid/TabletopHexGrid";
import { TabletopCanvasInputContext } from "../controls/TabletopCanvasInputContext";
import { TabletopCanvasInputMode } from "../controls/TabletopCanvasInputMode";
import TabletopElement from "../objects/element/TabletopElement";
import TabletopCanvasKeyEvent from "../controls/events/TabletopCanvasKeyEvent";
import TabletopCanvasMouseEvent from "../controls/events/TabletopCanvasMouseEvent";
import TabletopCanvasWheelEvent from "../controls/events/TabletopCanvasWheelEvent";
import TabletopCanvasDrawTerrainController from "../controls/controllers/TabletopCanvasDrawTerrainController";
import TabletopCanvasNavigationController from "../controls/controllers/TabletopCanvasNavigationController";
import TabletopCanvasController from "../controls/controllers/TabletopCanvasController";
import TabletopCanvasManipulateTerrainController from "../controls/controllers/TabletopCanvasManipulateTerrainController";
import TabletopCanvasManipulateTokenController from "../controls/controllers/TabletopCanvasManipulateTokenController";
import TabletopCanvasCreateTokenController from "../controls/controllers/TabletopCanvasCreateTokenController";

export default function TabletopCanvas(props: {
    className?: string;
    width: number;
    height: number;
    universe: TabletopUniverse;
    viewport: TabletopViewport;
}): ReactElement {
    const { className, width, height, universe, viewport } = props;
    const { state: canvasInput, setState: setCanvasInput } = useContext(
        TabletopCanvasInputContext,
    );
    const navigationController = useMemo(
        () => new TabletopCanvasNavigationController(viewport, universe),
        [viewport, universe],
    );
    const [activeController, setActiveController] =
        useState<TabletopCanvasController>(navigationController);

    const assignActiveController = useCallback(
        (controller: TabletopCanvasController) => {
            if (
                activeController != null &&
                activeController !== navigationController
            ) {
                activeController.dispose();
            }

            setActiveController(controller);
        },
        [activeController, navigationController],
    );

    /* Active controller interfaces with canvas toolbar. Updates to active controller should not trigger this effect */
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        switch (canvasInput.inputMode) {
            case TabletopCanvasInputMode.DRAW_TERRAIN:
                if (
                    activeController instanceof
                    TabletopCanvasDrawTerrainController
                ) {
                    activeController.shapeRenderer =
                        canvasInput.palette.terrainShape ?? null;
                } else {
                    if (
                        activeController != null &&
                        activeController !== navigationController
                    ) {
                        activeController.dispose();
                    }
                    assignActiveController(
                        new TabletopCanvasDrawTerrainController(
                            viewport,
                            universe,
                            canvasInput.palette.terrainShape ?? undefined,
                        ),
                    );
                }

                break;
            case TabletopCanvasInputMode.MANIPULATE_TERRAIN:
                assignActiveController(
                    new TabletopCanvasManipulateTerrainController(
                        viewport,
                        universe,
                    ),
                );
                break;
            case TabletopCanvasInputMode.CREATE_TOKEN:
                assignActiveController(
                    new TabletopCanvasCreateTokenController(viewport, universe),
                );
                break;
            case TabletopCanvasInputMode.MANIPULATE_TOKEN:
                assignActiveController(
                    new TabletopCanvasManipulateTokenController(
                        viewport,
                        universe,
                    ),
                );
                break;
            case TabletopCanvasInputMode.NAVIGATE_CANVAS:
            default:
                assignActiveController(navigationController);
        }
    }, [
        canvasInput.inputMode,
        canvasInput.palette.terrainShape,
        universe,
        viewport,
    ]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const canvasRef = useCanvas((context: CanvasRenderingContext2D) => {
        const canvasWidth = context.canvas.width;
        const canvasHeight = context.canvas.height;
        const timestamp = Date.now();

        context.fillStyle = "cyan";
        context.fillRect(0, 0, canvasWidth, canvasHeight);

        const deferredDraws: Array<TabletopElement> = [];
        universe.backgroundElements.forEach((element) => {
            if (element.isDrawable()) {
                if (element instanceof TabletopPositionalElement) {
                    if (viewport.overlaps(element)) {
                        element.draw(context, viewport, timestamp);
                    }
                } else {
                    element.draw(context, viewport, timestamp);
                }
            }
        });

        if (canvasInput.gridVisible) {
            if (
                universe.gridElement != null &&
                universe.gridElement.isDrawable()
            ) {
                universe.gridElement.draw(context, viewport, timestamp);
            }
        }

        universe.tokens.forEach((token) => {
            if (viewport.overlaps(token)) {
                token.draw(context, viewport, timestamp);
            }
        });

        if (deferredDraws.length > 0) {
            deferredDraws.forEach((element) => {
                if (element.isDrawable()) {
                    element.draw(context, viewport, timestamp);
                }
            });
        }

        viewport.draw(context, viewport, timestamp);
    });

    const [cursorUniversePosition, setCursorUniversePosition] =
        useState<Point2d | null>(null);

    /* const cursorCellCoord = useMemo(() => {
        const cellCoords =
            cursorUniversePosition == null || universe.gridElement == null
                ? null
                : universe.gridElement.getCellCoordinatesFromUniversePoint(
                      cursorUniversePosition,
                  );

        if (
            universe.gridElement != null &&
            universe.gridElement instanceof TabletopHexGrid
        ) {
            universe.gridElement.highlightCell = cellCoords;
        }

        return cellCoords;
    }, [cursorUniversePosition, universe.gridElement]); */

    const onCanvasMouseMove = (e: MouseEvent) => {
        const event = new TabletopCanvasMouseEvent(e, viewport);
        setCursorUniversePosition(event.universePosition);
        activeController.onCanvasMouseMove(event);

        viewport.cursorPosition = viewport.universePointToViewportPoint(
            event.universePosition,
        );
    };

    const onCanvasMouseDrag = (e: MouseEvent) => {
        activeController.onCanvasMouseDrag(
            new TabletopCanvasMouseEvent(e, viewport),
        );
    };

    const onCanvasMouseDown = (e: MouseEvent) => {
        activeController.onCanvasMouseDown(
            new TabletopCanvasMouseEvent(e, viewport),
        );
    };

    const onCanvasMouseUp = (e: MouseEvent) => {
        activeController.onCanvasMouseUp(
            new TabletopCanvasMouseEvent(e, viewport),
        );
    };

    const onCanvasMouseWheel = (e: WheelEvent) => {
        activeController.onCanvasMouseWheel(
            new TabletopCanvasWheelEvent(e, viewport),
        );
    };

    const onCanvasKeyPress = (e: KeyboardEvent) => {
        activeController.onCanvasKeyPress(new TabletopCanvasKeyEvent(e));
    };

    const onCanvasKeyDown = (e: KeyboardEvent) => {
        activeController.onCanvasKeyDown(new TabletopCanvasKeyEvent(e));
    };

    const onCanvasKeyUp = (e: KeyboardEvent) => {
        activeController.onCanvasKeyUp(new TabletopCanvasKeyEvent(e));
    };

    useTabletopCanvasInputHandler(canvasRef, {
        onMouseMove: onCanvasMouseMove,
        onMouseDrag: onCanvasMouseDrag,
        onMouseDown: onCanvasMouseDown,
        onMouseUp: onCanvasMouseUp,
        onMouseWheel: onCanvasMouseWheel,
        onKeyPress: onCanvasKeyPress,
        onKeyDown: onCanvasKeyDown,
        onKeyUp: onCanvasKeyUp,
    });

    return (
        <canvas
            className={className}
            ref={canvasRef}
            width={width}
            height={height}
            style={{ width: width, height: height }}
        />
    );
}
